import { DateTime } from 'luxon';
import {
  CommunicationTypeModel,
  ICommunicationTypeModel,
  IOrganizationListingModel,
  OrganizationListingModel,
  IStateModel,
  BrandModel,
  BreedModel,
  ClinicRoleModel,
  ClinicRoleTypeModel,
  IClinicRoleTypeModel,
  ExamObservationModel,
  IExamObservationModel,
  ExamResultTypeModel,
  IExamResultTypeModel,
  FeatureFlagModel,
  IFeatureFlagModel,
  GenderModel,
  IGenderModel,
  ILicenseTypeModel,
  LicenseTypeModel,
  PartnerModel,
  IPetAlertModel,
  PetAlertModel,
  IPetColorModel,
  PetColorModel,
  IReferralTypeModel,
  ReferralTypeModel,
  IRelationshipType,
  RelationshipTypeModel,
  IScheduleBlockTypeModel,
  ISpeciesModel,
  SpeciesModel,
  StateModel,
  ITemplateModel,
  TemplateModel,
  ITemplateTypeModel,
  TemplateTypeModel,
  IVisitCompletionTypeModel,
  VisitCompletionTypeModel,
  IVisitStatusModel,
  VisitStatusModel,
  IBrandModel,
  IPartnerModel,
  IBreedModel,
  IPronounModel,
  IExamCategoryModel,
  IClinicRoleModel,
  IDiagnosisModel,
  DiagnosisModel,
  RecommendationTypeModel,
  AutoRecommendationTypeModel,
  AssessmentReferralModel,
  IPaymentMethodModel,
  ISalesSubCategoryModel,
  SalesSubCategoryModel,
  BusinessLineModel,
  IBusinessLineModel,
  QuestionTypeModel,
  IOrganizationVersionModel,
  OrganizationVersionModel,
  IConsentModel,
  ConsentModel,
  ISalesItemAttributeTypeModel,
  SalesItemAttributeTypeModel,
  ISalesItemAttributeProcessingTypeModel,
  SalesItemAttributeProcessingTypeModel,
  ISalesItemTypeModel,
  SalesItemTypeModel,
  IDiscountTypeModel,
  DiscountTypeModel,
  DiagnosisStatusModel,
  IAutoAddToVisitTypeModel,
  AutoAddToVisitTypeModel,
  IPrescriptionLabelModel,
  PrescriptionLabelModel,
  IAdditionalInfoLabelModel,
  AdditionalInfoLabelModel,
  IPrescriptionEventTypeModel,
  PrescriptionEventTypeModel,
  IPrescriptionFulfillmentModel,
  PrescriptionFulfillmentModel,
  IPrescriptionStatusModel,
  PrescriptionStatusModel,
  TraitModel,
  ITraitModel,
  IUnitOfMeasureModel,
  UnitOfMeasureModel,
  ICallCodeModel,
  ICallOriginModel,
  CallOriginModel,
  CallCodeModel,
  SalesCategoryModel,
  ISalesCategoryModel,
  IVaccineNameModel,
  VaccineNameModel,
  IVaccineManufacturerModel,
  IVaccineVirusTypeModel,
  IVaccineAdministeredModel,
  VaccineManufacturerModel,
  VaccineVirusTypeModel,
  VaccineAdministeredModel,
  MedicationTypeModel,
  IMedicationTypeModel,
  schedule,
  ScheduleBlockTypeModel,
  ScheduleBlockTypeUIModel,
  InvoiceDiscrepancyReasonModel,
  IInvoiceDiscrepancyReasonModel,
  IPrescriptionPharmacyModel,
  PrescriptionPharmacyModel,
  IReportingGroupSalesItemModel,
  ReportingGroupSalesItemModel,
  ILabOrderStatusModel,
  LabOrderStatusModel,
  IHospitalPrescriptionRequestSourceModel,
  HospitalPrescriptionRequestSourceModel,
  IDiagnosisStatusModel,
  ISalesRequirementModel,
  SalesRequirementModel,
  IHospitalPrescriptionEventTypeModel,
  HospitalPrescriptionEventTypeModel,
} from '.';
import {
  AuthOperationModel,
  AuthRoleModel,
  IAuthOperationModel,
  IAuthRoleModel,
} from './Security';
import {
  AuthApplicationKeys,
  AuthOperationRoleNames,
  BusinessLineEnum,
  EntityScheduleType,
  Templates,
} from './Enums';
import { PronounModel } from './PronounModel';
import { ExamCategoryModel } from './ExamCategoryModel';
import {
  ClinicTaskTypeModel,
  IClinicTaskTypeModel,
} from './ClinicTaskTypeModel';
import { ISelectOption } from '@common/components/forms';
import { orderBy } from 'lodash';
import { PaymentMethodModel } from './PaymentMethodModel';
import { ConsentTypeModel, IConsentTypeModel } from './ConsentTypeModel';
import {
  HospitalPrescriptionRefillStatusModel,
  IHospitalPrescriptionRefillStatusModel,
} from './HospitalPrescriptionRefillStatusModel';
import { AuthOperationUINames } from './Enums/AuthOperationStrings';
import {
  HospitalPrescriptionStatusModel,
  IHospitalPrescriptionStatusModel,
} from './HospitalPrescriptionStatusModel';
import {
  IRabiesInformationModel,
  RabiesInformationModel,
} from './RabiesInformationModel';
import {
  ClinicCancellationReasonModel,
  IClinicCancellationReasonModel,
} from './ClinicCancellationReasonModel';
import { VisitTagModel } from '@common/models/VisitTagModel';
import {
  IReminderSourceModel,
  ReminderSourceModel,
} from '@common/models/ReminderSourceModel';
import {
  IReminderDeactivationReasonModel,
  ReminderDeactivationReasonModel,
} from '@common/models/ReminderDeactivationReasonModel';

export interface IClientConfigurationModel {
  authApplicationId: number;
  authApplicationKey: string;
  authApplicationDescription: string;
  userId: number;
  veterinarianId: number | null;
  userIdentityName: string;
  arrivalToastTimeoutSeconds: number;
  authorizedOperations: string[];
  usersFeatureFlagIds: number[];
  clearPendingAfterDays: string | undefined;
  fullName: string;
  firstName: string;
  lastName: string;
  userInitials: string;
  emailAddress: string;
  systemEnvironment: string;
  shouldShowRemindersInVisitBuilder: boolean;
  pGRLookupEnabled: boolean;
  twilioLookupEnabled: boolean;
  vobRescheduleEnabled: boolean;
  hospitalDateRestrictionsOnAppointmentActionsDisabled: boolean;
  writtenHospitalPrescriptionsEnabled: boolean;
  currentUTCDateTime: string | undefined;
  timerIntervalMilliseconds: number;
  timerQuantizeSeconds: number;
  timerWarningThresholdSeconds: number;
  vetspireDocumentViewerUrl: string;
  templates: ITemplateModel[];
  brands: IBrandModel[];
  partners: IPartnerModel[];
  breeds: IBreedModel[];
  communicationTypes: ICommunicationTypeModel[];
  clinicRoles: IClinicRoleModel[];
  clinicRoleTypes: IClinicRoleTypeModel[];
  featureFlags: IFeatureFlagModel[];
  genders: IGenderModel[];
  petColors: IPetColorModel[];
  referralTypes: IReferralTypeModel[];
  species: ISpeciesModel[];
  visitStatuses: IVisitStatusModel[];
  petAlerts: IPetAlertModel[];
  clinicTaskTypes: IClinicTaskTypeModel[];
  diagnoses: IDiagnosisModel[];
  callCodes: ICallCodeModel[];
  callOrigins: ICallOriginModel[];
  // heartWormProducts: IHeartwormProductModel[]
  // fleaTickProducts: IFleaTickProductModel[]
  paymentMethods: IPaymentMethodModel[];
  // reportingGroups: IReportingGroupModel[]
  salesItemAttributeTypes: ISalesItemAttributeTypeModel[];
  salesItemAttributeProcessingTypes: ISalesItemAttributeProcessingTypeModel[];
  salesItemTypes: ISalesItemTypeModel[];
  discountTypes: IDiscountTypeModel[];
  salesCategories: ISalesCategoryModel[];
  salesSubCategories: ISalesSubCategoryModel[];
  states: IStateModel[];
  visitCompletionTypes: IVisitCompletionTypeModel[];
  examResultTypes: IExamResultTypeModel[];
  // mobileRigs: IMobileRigModel[]
  autoAddToVisitTypes: IAutoAddToVisitTypeModel[];
  prescriptionLabels: IPrescriptionLabelModel[];
  // followUpTypes: IFollowUpTypeModel[]
  additionalInfoLabels: IAdditionalInfoLabelModel[];
  authRoles: IAuthRoleModel[];
  authOperations: IAuthOperationModel[];
  organizationVersions: IOrganizationVersionModel[];
  currentCorpOrganization: IOrganizationListingModel;
  //visitTags: IVisitTags[]; //needs implemented
  templateTypes: ITemplateTypeModel[];
  examCategories: IExamCategoryModel[];
  examObservations: IExamObservationModel[];
  recommendationTypes: RecommendationTypeModel[];
  autoRecommendationTypes: AutoRecommendationTypeModel[];
  diagnosisStatuses: DiagnosisStatusModel[];
  assessmentReferrals: AssessmentReferralModel[];
  questionTypes: QuestionTypeModel[];
  //intakeCompletionStatuses: IIntakeCompletionStatusModel[]; //needs implemented
  clinicCancellationReasons: IClinicCancellationReasonModel[]; //needs implemented
  consentTypes: IConsentTypeModel[];
  consents: IConsentModel[];
  licenseTypes: ILicenseTypeModel[];
  prescriptionEventTypes: IPrescriptionEventTypeModel[];
  prescriptionFulfillments: IPrescriptionFulfillmentModel[];
  prescriptionStatuses: IPrescriptionStatusModel[];
  prescriptionPharmacies: IPrescriptionPharmacyModel[];
  pronouns: IPronounModel[];
  //TODO: Rename scheduleBlockTypes to vetAndTemplateScheduleBlockTypes, as it only contains blocks for those entity types now, avoided massive changes by renaming it.
  scheduleEntityTypes: schedule.IScheduleEntityTypeModel[];
  scheduleBlockTypes: IScheduleBlockTypeModel[];
  scheduleEntityBlockTypes: schedule.IScheduleEntityBlockTypeModel[]; //<- contains the schedule block type ids that are allowed for each ScheduleEntityTypeId (ie. what blocks are available for vets, for template, for technicians, for unassinged, etc.)
  relationshipTypes: IRelationshipType[];
  traits: ITraitModel[];
  businessLines: IBusinessLineModel[];
  unitOfMeasures: IUnitOfMeasureModel[];
  clinicBusinessLineOrganization: IOrganizationListingModel;
  hospitalBusinessLineOrganization: IOrganizationListingModel;
  hospitalPricingTierOrganizations: IOrganizationListingModel[];
  hospitalPrescriptionRefillStatuses: IHospitalPrescriptionRefillStatusModel[];
  hospitalPrescriptionStatuses: IHospitalPrescriptionStatusModel[];
  hospitalPrescriptionRequestSources: IHospitalPrescriptionRequestSourceModel[];
  hospitalPrescriptionEventTypes: IHospitalPrescriptionEventTypeModel[];
  vaccineNames: IVaccineNameModel[];
  vaccineManufacturers: IVaccineManufacturerModel[];
  vaccineVirusTypes: IVaccineVirusTypeModel[];
  vaccineAdministereds: IVaccineAdministeredModel[];
  rabiesInformations: IRabiesInformationModel[];
  medicationTypes: IMedicationTypeModel[];
  invoiceDiscrepancyReasons: IInvoiceDiscrepancyReasonModel[];
  shortClientSideCacheTimeToLive: number;
  mediumClientSideCacheTimeToLive: number;
  longClientSideCacheTimeToLive: number;
  baseUrlCSR: string;
  baseUrlGlobalAdmin: string;
  jitLabResultUrlTemplate: string;
  vobReferralUrl: string;
  templateMaxSize: number;
  visitTags: VisitTagModel[];
  reminderSources: IReminderSourceModel[];
  reminderDeactivationReasons: IReminderDeactivationReasonModel[];
  medicalHistoryGroupSalesItems: IReportingGroupSalesItemModel[];
  labOrderStatuses: ILabOrderStatusModel[];
  salesRequirements: ISalesRequirementModel[];
}

export class ClientConfigurationModel
  implements Record<keyof IClientConfigurationModel, any>
{
  currentAppBusinessLine: BusinessLineEnum;
  authApplicationId: number;
  authApplicationKey: string;
  authApplicationDescription: string;
  userId: number;
  veterinarianId: number | null;
  userIdentityName: string;
  arrivalToastTimeoutSeconds: number;
  authorizedOperations: string[];
  usersFeatureFlagIds: number[];
  clearPendingAfterDays: string | undefined;
  fullName: string;
  firstName: string;
  lastName: string;
  userInitials: string;
  emailAddress: string;
  systemEnvironment: string;
  shouldShowRemindersInVisitBuilder: boolean;
  pGRLookupEnabled: boolean;
  twilioLookupEnabled: boolean;
  vobRescheduleEnabled: boolean;
  hospitalDateRestrictionsOnAppointmentActionsDisabled: boolean;
  writtenHospitalPrescriptionsEnabled: boolean;
  currentUTCDateTime: DateTime | undefined;
  timerIntervalMilliseconds: number;
  timerQuantizeSeconds: number;
  timerWarningThresholdSeconds: number;
  vetspireDocumentViewerUrl: string;
  templates: TemplateModel[];
  brands: BrandModel[];
  partners: PartnerModel[];
  breeds: BreedModel[];
  diagnoses: DiagnosisModel[];
  communicationTypes: CommunicationTypeModel[];
  clinicRoles: ClinicRoleModel[];
  clinicRoleTypes: ClinicRoleTypeModel[];
  diagnosisStatuses: IDiagnosisStatusModel[];
  featureFlags: FeatureFlagModel[];
  genders: IGenderModel[];
  petColors: IPetColorModel[];
  referralTypes: ReferralTypeModel[];
  species: ISpeciesModel[];
  visitStatuses: IVisitStatusModel[];
  petAlerts: IPetAlertModel[];
  clinicTaskTypes: ClinicTaskTypeModel[];
  callCodes: ICallCodeModel[];
  callOrigins: ICallOriginModel[];
  // heartWormProducts: IHeartwormProductModel[]
  // fleaTickProducts: IFleaTickProductModel[]
  paymentMethods: IPaymentMethodModel[];
  // reportingGroups: IReportingGroupModel[]
  salesItemAttributeTypes: ISalesItemAttributeTypeModel[];
  salesItemAttributeProcessingTypes: ISalesItemAttributeProcessingTypeModel[];
  salesItemTypes: ISalesItemTypeModel[];
  discountTypes: IDiscountTypeModel[];
  salesCategories: ISalesCategoryModel[];
  salesSubCategories: ISalesSubCategoryModel[];
  states: IStateModel[];
  visitCompletionTypes: IVisitCompletionTypeModel[];
  examResultTypes: IExamResultTypeModel[];
  // mobileRigs: IMobileRigModel[]
  autoAddToVisitTypes: IAutoAddToVisitTypeModel[];
  prescriptionLabels: IPrescriptionLabelModel[];
  // followUpTypes: IFollowUpTypeModel[]
  additionalInfoLabels: IAdditionalInfoLabelModel[];
  authRoles: IAuthRoleModel[];
  authOperations: IAuthOperationModel[];
  organizationVersions: IOrganizationVersionModel[];
  currentCorpOrganization: IOrganizationListingModel;
  //visitTags: IVisitTags[]; //needs implemented
  templateTypes: ITemplateTypeModel[];
  examCategories: IExamCategoryModel[];
  examObservations: IExamObservationModel[];
  recommendationTypes: RecommendationTypeModel[];
  autoRecommendationTypes: AutoRecommendationTypeModel[];
  assessmentReferrals: AssessmentReferralModel[];
  questionTypes: QuestionTypeModel[];
  //intakeCompletionStatuses: IIntakeCompletionStatusModel[]; //needs implemented
  clinicCancellationReasons: IClinicCancellationReasonModel[]; //needs implemented
  consentTypes: IConsentTypeModel[];
  consents: ConsentModel[]; //needs implemented
  licenseTypes: ILicenseTypeModel[];
  prescriptionEventTypes: IPrescriptionEventTypeModel[];
  prescriptionFulfillments: IPrescriptionFulfillmentModel[];
  prescriptionStatuses: IPrescriptionStatusModel[];
  prescriptionPharmacies: IPrescriptionPharmacyModel[];
  pronouns: IPronounModel[];
  scheduleEntityTypes: schedule.IScheduleEntityTypeModel[];
  appScheduleEntityTypes: schedule.IScheduleEntityTypeModel[];
  scheduleBlockTypes: IScheduleBlockTypeModel[];
  scheduleEntityBlockTypes: schedule.ScheduleEntityBlockTypeModel[];
  allScheduleBlockTypes: IScheduleBlockTypeModel[];
  relationshipTypes: IRelationshipType[];
  traits: ITraitModel[];
  businessLines: IBusinessLineModel[];
  unitOfMeasures: IUnitOfMeasureModel[];
  clinicBusinessLineOrganization: IOrganizationListingModel;
  hospitalBusinessLineOrganization: IOrganizationListingModel;
  hospitalPricingTierOrganizations: IOrganizationListingModel[];
  hospitalPrescriptionRefillStatuses: IHospitalPrescriptionRefillStatusModel[];
  hospitalPrescriptionStatuses: IHospitalPrescriptionStatusModel[];
  hospitalPrescriptionRequestSources: IHospitalPrescriptionRequestSourceModel[];
  hospitalPrescriptionEventTypes: IHospitalPrescriptionEventTypeModel[];
  vaccineNames: IVaccineNameModel[];
  vaccineManufacturers: IVaccineManufacturerModel[];
  vaccineVirusTypes: IVaccineVirusTypeModel[];
  vaccineAdministereds: IVaccineAdministeredModel[];
  rabiesInformations: IRabiesInformationModel[];
  medicationTypes: IMedicationTypeModel[];
  invoiceDiscrepancyReasons: IInvoiceDiscrepancyReasonModel[];
  shortClientSideCacheTimeToLive: number;
  mediumClientSideCacheTimeToLive: number;
  longClientSideCacheTimeToLive: number;
  baseUrlCSR: string;
  baseUrlGlobalAdmin: string;
  jitLabResultUrlTemplate: string;
  vobReferralUrl: string;
  templateMaxSize: number;
  visitTags: VisitTagModel[];
  reminderSources: IReminderSourceModel[];
  reminderDeactivationReasons: IReminderDeactivationReasonModel[];
  medicalHistoryGroupSalesItems: IReportingGroupSalesItemModel[];
  labOrderStatuses: ILabOrderStatusModel[];
  salesRequirements: ISalesRequirementModel[];

  constructor(params?: Partial<IClientConfigurationModel>) {
    const {
      authApplicationId = 0,
      authApplicationKey = AuthApplicationKeys.PetPass,
      authApplicationDescription = 'PetPass',
      userId = 0,
      veterinarianId = null,
      userIdentityName = '',
      arrivalToastTimeoutSeconds = 0,
      authorizedOperations = [],
      usersFeatureFlagIds = [],
      clearPendingAfterDays = undefined,
      fullName = '',
      firstName = '',
      lastName = '',
      userInitials = '',
      emailAddress = '',
      systemEnvironment = 'DEVLOCAL',
      shouldShowRemindersInVisitBuilder = false,
      pGRLookupEnabled = false,
      twilioLookupEnabled = false,
      vobRescheduleEnabled = false,
      hospitalDateRestrictionsOnAppointmentActionsDisabled = false,
      writtenHospitalPrescriptionsEnabled = false,
      currentUTCDateTime = undefined,
      timerIntervalMilliseconds = 0,
      timerQuantizeSeconds = 0,
      timerWarningThresholdSeconds = 0,
      vetspireDocumentViewerUrl = '',
      templates = [],
      brands = [],
      partners = [],
      breeds = [],
      diagnoses = [],
      diagnosisStatuses = [],
      communicationTypes = [],
      clinicRoles = [],
      clinicRoleTypes = [],
      featureFlags = [],
      genders = [],
      petColors = [],
      referralTypes = [],
      species = [],
      salesRequirements = [],
      visitStatuses = [],
      petAlerts = [],
      clinicTaskTypes = [],
      callCodes = [],
      callOrigins = [],
      // heartWormProducts: IHeartwormProductModel[]
      // fleaTickProducts: IFleaTickProductModel[]
      paymentMethods = [],
      // reportingGroups: IReportingGroupModel[]
      salesItemAttributeTypes = [],
      salesItemAttributeProcessingTypes = [],
      salesItemTypes = [],
      discountTypes = [],
      salesCategories = [],
      salesSubCategories = [],
      states = [],
      visitCompletionTypes = [],
      examResultTypes = [],
      // mobileRigs: IMobileRigModel[]
      autoAddToVisitTypes = [],
      prescriptionLabels = [],
      // followUpTypes: IFollowUpTypeModel[]
      additionalInfoLabels = [],
      authRoles = [],
      authOperations = [],
      organizationVersions = [],
      currentCorpOrganization = {} as IOrganizationListingModel,
      //visitTags: IVisitTags[]; //needs implemented
      templateTypes = [],
      examCategories = [],
      examObservations = [],
      recommendationTypes = [],
      autoRecommendationTypes = [],
      assessmentReferrals = [],
      questionTypes = [],
      //intakeCompletionStatuses: IIntakeCompletionStatusModel[]; //needs implemented
      clinicCancellationReasons = [] as IClinicCancellationReasonModel[], //needs implemented
      consentTypes = [],
      consents = [], //needs implemented
      licenseTypes = [],
      prescriptionEventTypes = [],
      prescriptionFulfillments = [],
      prescriptionStatuses = [],
      prescriptionPharmacies = [],
      pronouns = [],
      scheduleEntityTypes = [],
      scheduleBlockTypes = [],
      scheduleEntityBlockTypes = [],
      relationshipTypes = [],
      businessLines = [],
      traits = [],
      unitOfMeasures = [],
      clinicBusinessLineOrganization = {} as IOrganizationListingModel,
      hospitalBusinessLineOrganization = {} as IOrganizationListingModel,
      hospitalPricingTierOrganizations = [] as IOrganizationListingModel[],
      hospitalPrescriptionRefillStatuses = [] as IHospitalPrescriptionRefillStatusModel[],
      hospitalPrescriptionStatuses = [] as IHospitalPrescriptionStatusModel[],
      hospitalPrescriptionRequestSources = [] as IHospitalPrescriptionRequestSourceModel[],
      hospitalPrescriptionEventTypes = [] as IHospitalPrescriptionEventTypeModel[],
      vaccineNames = [] as IVaccineNameModel[],
      vaccineManufacturers = [] as IVaccineManufacturerModel[],
      vaccineVirusTypes = [] as IVaccineVirusTypeModel[],
      vaccineAdministereds = [] as IVaccineAdministeredModel[],
      rabiesInformations = [] as IRabiesInformationModel[],
      medicationTypes = [] as IMedicationTypeModel[],
      invoiceDiscrepancyReasons = [] as IInvoiceDiscrepancyReasonModel[],
      shortClientSideCacheTimeToLive = 0,
      mediumClientSideCacheTimeToLive = 0,
      longClientSideCacheTimeToLive = 0,
      baseUrlCSR = '',
      baseUrlGlobalAdmin = '',
      jitLabResultUrlTemplate = '',
      vobReferralUrl = '',
      templateMaxSize = 10000,
      visitTags = [],
      reminderSources = [] as IReminderSourceModel[],
      reminderDeactivationReasons = [] as IReminderDeactivationReasonModel[],
      medicalHistoryGroupSalesItems = [] as IReportingGroupSalesItemModel[],
      labOrderStatuses = [] as ILabOrderStatusModel[],
    } = params ? params : {};

    this.authApplicationId = authApplicationId;
    this.authApplicationKey = authApplicationKey!;
    this.authApplicationDescription = authApplicationDescription!;

    switch (this.authApplicationKey?.toUpperCase() ?? 'unknown') {
      case AuthApplicationKeys.Admin:
        this.currentAppBusinessLine = BusinessLineEnum.Global;
        break;
      case AuthApplicationKeys.PetPass:
        this.currentAppBusinessLine = BusinessLineEnum.Clinic;
        break;
      case AuthApplicationKeys.SWIFT:
        this.currentAppBusinessLine = BusinessLineEnum.Hospital;
        break;
      default:
        throw 'Unkown authApplication';
    }

    this.userId = userId;
    this.veterinarianId = veterinarianId;
    this.userIdentityName = userIdentityName!;
    this.arrivalToastTimeoutSeconds = arrivalToastTimeoutSeconds;
    this.authorizedOperations = authorizedOperations;
    this.usersFeatureFlagIds = usersFeatureFlagIds;
    this.clearPendingAfterDays = clearPendingAfterDays
      ? clearPendingAfterDays
      : undefined;
    this.fullName = fullName;
    this.firstName = firstName;
    this.lastName = lastName;
    this.userInitials = userInitials;
    this.emailAddress = emailAddress;
    this.systemEnvironment = systemEnvironment;
    this.shouldShowRemindersInVisitBuilder = shouldShowRemindersInVisitBuilder;
    this.pGRLookupEnabled = pGRLookupEnabled;
    this.twilioLookupEnabled = twilioLookupEnabled;
    this.vobRescheduleEnabled = vobRescheduleEnabled;
    this.hospitalDateRestrictionsOnAppointmentActionsDisabled =
      hospitalDateRestrictionsOnAppointmentActionsDisabled ? true : false;
    this.writtenHospitalPrescriptionsEnabled =
      writtenHospitalPrescriptionsEnabled ? true : false;
    this.currentUTCDateTime = currentUTCDateTime
      ? DateTime.fromISO(currentUTCDateTime)
      : undefined;
    this.timerIntervalMilliseconds = timerIntervalMilliseconds;
    this.timerQuantizeSeconds = timerQuantizeSeconds;
    this.timerWarningThresholdSeconds = timerWarningThresholdSeconds;
    this.vetspireDocumentViewerUrl = vetspireDocumentViewerUrl;
    this.templates = templates.map((t) => new TemplateModel(t));
    this.brands = brands.map((b) => new BrandModel(b));
    this.partners = partners.map((p) => new PartnerModel(p));
    this.breeds = breeds.map((b) => new BreedModel(b));
    this.diagnoses = diagnoses.map((d) => new DiagnosisModel(d));
    this.communicationTypes = communicationTypes.map(
      (c) => new CommunicationTypeModel(c),
    );
    this.clinicRoles = clinicRoles.map((cr) => new ClinicRoleModel(cr));
    this.clinicRoleTypes = clinicRoleTypes.map(
      (crt) => new ClinicRoleTypeModel(crt),
    );
    this.featureFlags = featureFlags.map((ff) => new FeatureFlagModel(ff));
    this.genders = genders.map((g) => new GenderModel(g));
    this.petColors = petColors.map((pc) => new PetColorModel(pc));
    this.referralTypes = referralTypes.map((rft) => new ReferralTypeModel(rft));
    this.species = species.map((sp) => new SpeciesModel(sp));
    this.salesRequirements = salesRequirements.map(
      (sr) => new SalesRequirementModel(sr),
    );
    this.visitStatuses = visitStatuses.map((vs) => new VisitStatusModel(vs));
    this.petAlerts = petAlerts.map((pa) => new PetAlertModel(pa));
    this.clinicTaskTypes = clinicTaskTypes.map(
      (ctt) => new ClinicTaskTypeModel(ctt),
    );
    this.callCodes = callCodes.map((cc) => new CallCodeModel(cc));
    this.callOrigins = callOrigins.map((co) => new CallOriginModel(co));
    // heartWormProducts: IHeartwormProductModel[]
    // fleaTickProducts: IFleaTickProductModel[]
    this.paymentMethods = paymentMethods.map((p) => new PaymentMethodModel(p));
    // reportingGroups: IReportingGroupModel[]
    this.salesItemAttributeTypes = salesItemAttributeTypes.map(
      (sa) => new SalesItemAttributeTypeModel(sa),
    );
    this.salesItemAttributeProcessingTypes =
      salesItemAttributeProcessingTypes.map(
        (sa) => new SalesItemAttributeProcessingTypeModel(sa),
      );
    this.salesItemTypes = salesItemTypes.map(
      (st) => new SalesItemTypeModel(st),
    );
    this.discountTypes = discountTypes.map((dt) => new DiscountTypeModel(dt));
    this.salesCategories = salesCategories.map(
      (sp) => new SalesCategoryModel(sp),
    );
    this.salesSubCategories = salesSubCategories.map(
      (sp) => new SalesSubCategoryModel(sp),
    );
    this.states = states.map((st) => new StateModel(st));
    this.visitCompletionTypes = visitCompletionTypes.map(
      (vct) => new VisitCompletionTypeModel(vct),
    );
    this.examResultTypes = examResultTypes.map(
      (ert) => new ExamResultTypeModel(ert),
    );
    // mobileRigs: IMobileRigModel[]
    this.autoAddToVisitTypes = autoAddToVisitTypes.map(
      (at) => new AutoAddToVisitTypeModel(at),
    );
    this.prescriptionLabels = prescriptionLabels.map(
      (pr) => new PrescriptionLabelModel(pr),
    );
    // followUpTypes: IFollowUpTypeModel[]
    this.additionalInfoLabels = additionalInfoLabels.map(
      (ad) => new AdditionalInfoLabelModel(ad),
    );
    this.authRoles = authRoles.map((ar) => new AuthRoleModel(ar));
    this.authOperations = authOperations.map(
      (ao) => new AuthOperationModel(ao),
    );
    this.organizationVersions = organizationVersions.map(
      (ov) => new OrganizationVersionModel(ov),
    );
    this.currentCorpOrganization = new OrganizationListingModel(
      currentCorpOrganization,
    );
    //visitTags: IVisitTags[]; //needs implemented
    this.templates = templates.map((t) => new TemplateModel(t));
    //visitTags: IVisitTags[]; //needs implemented
    this.templateTypes = templateTypes.map((tt) => new TemplateTypeModel(tt));
    this.examCategories = examCategories.map((ec) => new ExamCategoryModel(ec));
    this.examObservations = examObservations.map(
      (eo) => new ExamObservationModel(eo),
    );
    this.recommendationTypes = recommendationTypes.map(
      (rt) => new RecommendationTypeModel(rt),
    );
    this.autoRecommendationTypes = autoRecommendationTypes.map(
      (art) => new AutoRecommendationTypeModel(art),
    );
    this.assessmentReferrals = assessmentReferrals.map(
      (ar) => new AssessmentReferralModel(ar),
    );
    this.questionTypes = questionTypes.map((qt) => new QuestionTypeModel(qt));
    //intakeCompletionStatuses: IIntakeCompletionStatusModel[]; //needs implemented
    this.clinicCancellationReasons = clinicCancellationReasons.map(
      (ccr) => new ClinicCancellationReasonModel(ccr),
    );
    this.consentTypes = consentTypes.map((c) => new ConsentTypeModel(c));
    this.consents = consents.map((c) => new ConsentModel(c));
    this.licenseTypes = licenseTypes.map((lt) => new LicenseTypeModel(lt));
    this.prescriptionEventTypes = prescriptionEventTypes.map(
      (p) => new PrescriptionEventTypeModel(p),
    );
    this.prescriptionFulfillments = prescriptionFulfillments.map(
      (p) => new PrescriptionFulfillmentModel(p),
    );
    this.prescriptionStatuses = prescriptionStatuses.map(
      (p) => new PrescriptionStatusModel(p),
    );
    this.prescriptionPharmacies = prescriptionPharmacies.map(
      (p) => new PrescriptionPharmacyModel(p),
    );
    this.pronouns = pronouns.map((p) => new PronounModel(p));
    this.scheduleEntityTypes = scheduleEntityTypes.map(
      (set) => new schedule.ScheduleEntityTypeModel(set),
    );
    this.appScheduleEntityTypes = this.scheduleEntityTypes.filter(
      (sebt) => sebt.businessLineId === this.currentAppBusinessLine,
    );
    this.allScheduleBlockTypes = scheduleBlockTypes.map(
      (sbt) => new ScheduleBlockTypeModel(sbt),
    );
    this.scheduleEntityBlockTypes = scheduleEntityBlockTypes.map(
      (sebt) => new schedule.ScheduleEntityBlockTypeModel(sebt),
    );
    this.scheduleBlockTypes = this.getScheduleBlockTypesForEntity([
      EntityScheduleType.Veterinarian,
      EntityScheduleType.Template,
    ]);
    this.relationshipTypes = relationshipTypes.map(
      (rt) => new RelationshipTypeModel(rt),
    );
    this.traits = traits.map((trait) => new TraitModel(trait));
    this.businessLines = businessLines.map((bl) => new BusinessLineModel(bl));
    this.unitOfMeasures = unitOfMeasures.map(
      (um) => new UnitOfMeasureModel(um),
    );
    this.clinicBusinessLineOrganization = new OrganizationListingModel(
      clinicBusinessLineOrganization,
    );
    this.hospitalBusinessLineOrganization = new OrganizationListingModel(
      hospitalBusinessLineOrganization,
    );
    this.hospitalPricingTierOrganizations =
      hospitalPricingTierOrganizations.map(
        (o) => new OrganizationListingModel(o),
      );
    this.hospitalPrescriptionRefillStatuses =
      hospitalPrescriptionRefillStatuses.map(
        (h) => new HospitalPrescriptionRefillStatusModel(h),
      );
    this.hospitalPrescriptionStatuses = hospitalPrescriptionStatuses.map(
      (h) => new HospitalPrescriptionStatusModel(h),
    );
    this.hospitalPrescriptionRequestSources =
      hospitalPrescriptionRequestSources.map(
        (h) => new HospitalPrescriptionRequestSourceModel(h),
      );
    this.hospitalPrescriptionEventTypes = hospitalPrescriptionEventTypes.map(
      (p) => new HospitalPrescriptionEventTypeModel(p),
    );
    this.vaccineNames = vaccineNames.map((vn) => new VaccineNameModel(vn));
    this.vaccineManufacturers = vaccineManufacturers.map(
      (vn) => new VaccineManufacturerModel(vn),
    );
    this.vaccineVirusTypes = vaccineVirusTypes.map(
      (vn) => new VaccineVirusTypeModel(vn),
    );
    this.vaccineAdministereds = vaccineAdministereds.map(
      (vn) => new VaccineAdministeredModel(vn),
    );
    this.rabiesInformations = rabiesInformations.map(
      (ri) => new RabiesInformationModel(ri),
    );
    this.medicationTypes = medicationTypes.map(
      (md) => new MedicationTypeModel(md),
    );
    this.invoiceDiscrepancyReasons = invoiceDiscrepancyReasons.map(
      (idr) => new InvoiceDiscrepancyReasonModel(idr),
    );
    this.shortClientSideCacheTimeToLive = shortClientSideCacheTimeToLive;
    this.mediumClientSideCacheTimeToLive = mediumClientSideCacheTimeToLive;
    this.diagnosisStatuses = diagnosisStatuses.map(
      (ds) => new DiagnosisStatusModel(ds),
    );
    this.longClientSideCacheTimeToLive = longClientSideCacheTimeToLive;
    this.baseUrlCSR = baseUrlCSR;
    this.baseUrlGlobalAdmin = baseUrlGlobalAdmin;
    this.jitLabResultUrlTemplate = jitLabResultUrlTemplate;
    this.vobReferralUrl = vobReferralUrl;
    this.templateMaxSize = templateMaxSize;
    this.visitTags = visitTags.map((vt) => new VisitTagModel(vt));
    this.reminderSources = reminderSources.map(
      (rs) => new ReminderSourceModel(rs),
    );
    this.reminderDeactivationReasons = reminderDeactivationReasons.map(
      (rdr) => new ReminderDeactivationReasonModel(rdr),
    );
    this.medicalHistoryGroupSalesItems = medicalHistoryGroupSalesItems.map(
      (mhgsi) => new ReportingGroupSalesItemModel(mhgsi),
    );
    this.labOrderStatuses = labOrderStatuses.map(
      (los) => new LabOrderStatusModel(los),
    );
  }

  userHasRole(roleName: AuthOperationRoleNames | string) {
    const inRoles = _.intersectionWith(
      this.authRoles.filter((r) => _.isEqual(r.description, roleName)),
      this.authorizedOperations,
      (roleModel, authOp) => _.isEqual(roleModel.roleAuthOperationName, authOp),
    );
    return inRoles.length > 0;
  }

  selectOptions = {
    authRoles: (): ISelectOption[] =>
      orderBy(this.authRoles, 'description').map((ar) => ({
        label: ar.description,
        value: ar.authRoleId,
      })),
    clinicTaskTypes: (): ISelectOption[] =>
      this.clinicTaskTypes.map((ctt) => ({
        label: ctt.name,
        value: ctt.clinicTaskTypeId,
      })),
    labOrderStatuses: (): ISelectOption[] =>
      this.labOrderStatuses.map((los) => ({
        label: los.name,
        value: los.labOrderStatusId,
      })),
    hospitalPrescriptionRefillStatuses: (): ISelectOption[] =>
      this.hospitalPrescriptionRefillStatuses.map((h) => ({
        label: h.name,
        value: h.hospitalPrescriptionRefillStatusId,
      })),
    hospitalPrescriptionStatuses: (): ISelectOption[] =>
      this.hospitalPrescriptionStatuses.map((h) => ({
        label: h.name,
        value: h.hospitalPrescriptionStatusId,
      })),
    hospitalPrescriptionRequestSources: (): ISelectOption[] =>
      this.hospitalPrescriptionRequestSources.map((h) => ({
        label: h.name,
        value: h.hospitalPrescriptionRequestSourceId,
      })),
    prescriptionPharmacies: (): ISelectOption[] =>
      this.prescriptionPharmacies.map((p) => ({
        label: p.name,
        value: p.prescriptionPharmacyId,
      })),
    hospitalAllPrescriptionEvents: (): ISelectOption[] =>
      this.hospitalPrescriptionEventTypes.map((h) => ({
        label: h.name,
        value: h.hospitalPrescriptionEventTypeId,
      })),
  };

  getPricingTierOrganization(orgId: number) {
    const org = _.first(
      this.hospitalPricingTierOrganizations.filter(
        (a) => a.organizationId === orgId,
      ),
    );
    return org;
  }

  IsDVM = () => this.userHasRole(AuthOperationRoleNames.Veterinarian);

  HasUIFlag = (opName: AuthOperationUINames) => {
    return this.authorizedOperations.find((op) => {
      return op.toLowerCase() === opName.toLowerCase();
    })
      ? true
      : false;
  };

  hasFeatureFlag = (featureFlagId: number) => {
    return this.usersFeatureFlagIds.find((ff) => {
      return _.isEqual(ff, featureFlagId);
    })
      ? true
      : false;
  };

  /**
   * Returns all the existing schedule blocks that are valid for a ScheduleEntityType or Array of ScheduleEntityTypes.
   * @param entityType A single NewEntityScheduleType or an Array of them to include all the Block Types for Each Entity Type in the array.
   * @returns
   */
  getScheduleBlockTypesForEntity = (
    entityType: EntityScheduleType | EntityScheduleType[],
  ) => {
    let entityTypeArray = Array.isArray(entityType) ? entityType : [entityType];
    const filteredItems = _.filter(this.scheduleEntityBlockTypes, (item) =>
      _.includes(entityTypeArray, item.scheduleEntityTypeId),
    );
    const groupedByBlockTypeId = _.groupBy(
      filteredItems,
      'scheduleBlockTypeId',
    );
    const allowedBlockTypeIds = _.filter(
      _.keys(groupedByBlockTypeId),
      (e) => groupedByBlockTypeId[e].length === entityTypeArray.length,
    ).map((a) => parseInt(a));
    return _.filter(this.allScheduleBlockTypes, (sbt) =>
      allowedBlockTypeIds.includes(sbt.scheduleBlockTypeId),
    ).map((e) => new ScheduleBlockTypeUIModel({ ...e, isDisabled: false }));
  };

  getEnabledTemplate = (templateId: number): boolean => {
    return this.templates.some((t) => t.templateId === templateId && t.enabled);
  };

  getTemplateValue = (id: Templates) =>
    this.templates.find((t) => t.templateId === id)?.templateValue ?? '';
}
